<template>
  <div class="auth-page">
    <!-- Background elements -->
    <div class="bg-glow"></div>
    <div class="bg-grid"></div>
    
    <!-- Navigation -->
    <nav class="navbar">
      <router-link to="/" class="nav-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <router-link to="/login" class="btn-login">Login</router-link>
    </nav>

    <!-- Reset Password Form -->
    <div class="auth-container">
      <div class="auth-card">
        <!-- Request sent (forgot-password success) -->
        <div v-if="emailSent" class="status-content">
          <div class="status-icon success">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </div>
          <h1>Check your email</h1>
          <p>If an account exists for that email, we've sent a password reset link. It expires in 1 hour.</p>
          <router-link to="/login" class="btn-primary">Back to Login</router-link>
        </div>

        <!-- Success State (reset completed) -->
        <div v-else-if="success" class="status-content">
          <div class="status-icon success">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1>Password Reset!</h1>
          <p>Your password has been successfully reset. You can now log in with your new password.</p>
          <router-link to="/login" class="btn-primary">Go to Login</router-link>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="status-content">
          <div class="status-icon error">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h1>Reset Failed</h1>
          <p>{{ error }}</p>
          <router-link to="/login" class="btn-secondary">Back to Login</router-link>
        </div>

        <!-- Step 1: Request reset (no token) -->
        <div v-else-if="!token">
          <div class="auth-header">
            <h1>Forgot password?</h1>
            <p>Enter your email and we'll send you a link to reset your password.</p>
          </div>
          <form @submit.prevent="requestReset">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                v-model="email"
                placeholder="you@example.com"
                required
              />
            </div>
            <p v-if="formError" class="error-message">{{ formError }}</p>
            <button type="submit" class="btn-primary" :disabled="loading">
              {{ loading ? 'Sending...' : 'Send reset link' }}
            </button>
          </form>
        </div>

        <!-- Step 2: Set new password (has token) -->
        <div v-else>
          <div class="auth-header">
            <h1>Reset Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form @submit.prevent="resetPassword">
            <div class="form-group">
              <label for="password">New Password</label>
              <input 
                type="password" 
                id="password"
                v-model="password" 
                placeholder="••••••••"
                minlength="8"
                required
              />
              <p class="input-hint">At least 8 characters with a letter and number</p>
            </div>
            
            <div class="form-group">
              <label for="confirmPassword">Confirm Password</label>
              <input 
                type="password" 
                id="confirmPassword"
                v-model="confirmPassword" 
                placeholder="••••••••"
                required
              />
            </div>
            
            <p v-if="formError" class="error-message">{{ formError }}</p>
            
            <button type="submit" class="btn-primary" :disabled="loading">
              {{ loading ? 'Resetting...' : 'Reset Password' }}
            </button>
          </form>

          <p class="auth-footer">
            Remember your password? <router-link to="/login">Sign in</router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios, { formatApiErrorMessage } from '@/axios';
import AppLogo from '@/components/AppLogo.vue';

export default {
  name: 'ResetPassword',
  components: { AppLogo },
  data() {
    return {
      email: '',
      password: '',
      confirmPassword: '',
      loading: false,
      success: false,
      emailSent: false,
      error: null,
      formError: null,
    };
  },
  computed: {
    token() {
      return this.$route.query.token;
    },
  },
  mounted() {
    // No error when no token - we show the "forgot password" email form instead
  },
  methods: {
    async requestReset() {
      this.formError = null;
      if (!this.email || !this.email.trim()) {
        this.formError = 'Please enter your email.';
        return;
      }
      this.loading = true;
      try {
        await axios.post('/api/auth/forgot-password', { email: this.email.trim() });
        this.emailSent = true;
      } catch (err) {
        this.formError = formatApiErrorMessage(err, 'Something went wrong. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    async resetPassword() {
      this.formError = null;
      
      if (this.password !== this.confirmPassword) {
        this.formError = 'Passwords do not match';
        return;
      }
      
      // Validate password strength
      const errors = [];
      if (this.password.length < 8) {
        errors.push("Password must be at least 8 characters");
      }
      if (!/[A-Za-z]/.test(this.password)) {
        errors.push("Password must contain at least one letter");
      }
      if (!/[0-9]/.test(this.password)) {
        errors.push("Password must contain at least one number");
      }
      if (errors.length > 0) {
        this.formError = errors.join(". ");
        return;
      }
      
      this.loading = true;
      
      try {
        await axios.post('/api/auth/reset-password', {
          token: this.token,
          password: this.password,
        });
        this.success = true;
      } catch (err) {
        this.error = formatApiErrorMessage(err, 'Failed to reset password. The link may have expired.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.auth-page {
  min-height: 100vh;
  background: #0a0a0f;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
  filter: blur(60px);
  pointer-events: none;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

/* Navbar */
.navbar {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
}

.nav-logo {
  text-decoration: none;
}

.logo-img {
  height: 56px;
}

.btn-login {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s;
}

.btn-login:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.2);
}

/* Auth Container */
.auth-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  backdrop-filter: blur(10px);
}

.auth-header {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.auth-header p {
  color: #71717a;
  font-size: 15px;
}

/* Status Content */
.status-content {
  text-align: center;
}

.status-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.status-icon.success {
  background: rgba(34, 197, 94, 0.1);
  color: #4ade80;
}

.status-icon.error {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
}

.status-content h1 {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.75rem;
}

.status-content p {
  color: #71717a;
  font-size: 15px;
  margin-bottom: 2rem;
  line-height: 1.6;
}

/* Form */
.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  color: #a1a1aa;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  transition: all 0.2s;
}

.form-group input::placeholder {
  color: #52525b;
}

.form-group input:focus {
  outline: none;
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.input-hint {
  color: #52525b;
  font-size: 12px;
  margin-top: 6px;
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 1rem;
  text-align: center;
}

.btn-primary {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: block;
  text-align: center;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(139, 92, 246, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  width: 100%;
  padding: 14px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: block;
  text-align: center;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Footer */
.auth-footer {
  text-align: center;
  margin-top: 2rem;
  color: #71717a;
  font-size: 14px;
}

.auth-footer a {
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 500;
}

.auth-footer a:hover {
  color: #a78bfa;
}
</style>
